import { useMemo } from 'react';
import { ReportStatus } from '../TitleArea';

/**
 * 報告書ステータスを管理するカスタムフック。
 *
 * @typedef {Object} ReportStatus
 * @property {Object} reportStatusSelections - 報告書ステータス選択肢
 *
 * @function
 * @param {string} currentStatus - 現在の報告書ステータス。
 * @param {string} editStatus - 現在選択中の報告書ステータス。
 * @param {func} formatMessage - 国際化関数
 * @returns {ReportStatusHook} - 報告書ステータス関連の状態と操作関数
 */
const useReportStatus = (currentStatus, editStatus, formatMessage) => {
  const statusUpdateConfirmationInfo = useMemo(() => {
    if (editStatus === undefined || editStatus === ReportStatus.create) {
      return {};
    }
    const statusName = formatMessage({ id: `report.status.${editStatus}` });
    const headerText = formatMessage(
      { id: 'report.edit.status.confirmation.header' },
      { statusName },
    );
    const messageText = formatMessage({
      id: `report.edit.status.confirmation.message.${editStatus}`,
    });
    const buttonText = formatMessage(
      { id: 'report.edit.status.confirmation.button.ok' },
      { statusName },
    );
    return { headerText, messageText, buttonText };
  }, [editStatus, formatMessage]);

  const reportStatusSelections = useMemo(() => {
    const AllSelections = [
      {
        value: ReportStatus.create,
        text: formatMessage({
          id: 'report.status.create',
          defaultMessage: '下書き',
        }),
      },
      {
        value: ReportStatus.published,
        text: formatMessage({
          id: 'report.status.published',
          defaultMessage: '共有',
        }),
      },
      {
        value: ReportStatus.complete,
        text: formatMessage({
          id: 'report.status.complete',
          defaultMessage: '完了',
        }),
      },
    ];
    // publishedの場合はcreateを除く
    if (currentStatus === ReportStatus.published) {
      return AllSelections.filter(({ value }) => value !== ReportStatus.create);
    }
    // 上記以外（completeは当画面に来ないのでcreateのみ）はすべて返却する
    return AllSelections;
  }, [currentStatus, formatMessage]);

  return { reportStatusSelections, statusUpdateConfirmationInfo };
};

export default useReportStatus;
